import type { ComponentProps, FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { EntityLink } from "scmp-app/components/entity-link";
import type { sectionEditorPicksContentItemContent$key } from "scmp-app/queries/__generated__/sectionEditorPicksContentItemContent.graphql";

import {
  ArticleNumberIndex,
  Container,
  Headline,
  StyledContentCoverImage,
  StyledEntityLink,
} from "./styles";

type Props = ContentItemRenderVariantProps & {
  className?: string;
  hideImageOnMobile?: boolean;
  index: number;
  onClick?: ComponentProps<typeof EntityLink>["onClick"];
  reference: sectionEditorPicksContentItemContent$key;
};

const HEADLINE_OPTIONS = {
  hideTooltip: true,
  preferSocialHeadline: true,
};

const RESPONSIVE_VARIANTS = {
  desktopUp: "size540x360",
  mobileUp: "size540x360",
  tabletUp: "size540x360",
} as const;

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  hideImageOnMobile,
  index,
  onClick,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment sectionEditorPicksContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...contentCoverImageContent @arguments(withSize540x360: true)
        ...hooksContentItemProvidersContent @arguments(withHeadline: true, withCoverImage: true)
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(content);

  return (
    <Container className={className}>
      <StyledEntityLink
        $hideOnMobile={hideImageOnMobile}
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <StyledContentCoverImage reference={content} responsiveVariants={RESPONSIVE_VARIANTS} />
        <ArticleNumberIndex>{index}</ArticleNumberIndex>
        <Headline>{providers.headline(HEADLINE_OPTIONS)}</Headline>
      </StyledEntityLink>
    </Container>
  );
};

Component.displayName = "ContentItemSectionEditorPicks";

export const ContentItemSectionEditorPicks = Component;
